import { useQuery } from 'data-fetching-client';
import Products from 'merchandise-lib/constants/Product';
import { useCallback, useMemo } from 'react';
import { GET_PRODUCTS_V2_QUERY } from '../../../../__generated__/chirp/com/hubspot/buying/rpc/ProductRpc.dfc.new';
import { GET_PUBLIC_PRODUCTS_V2_QUERY } from '../../../../__generated__/chirp/com/hubspot/buying/rpc/PublicProductRpc.dfc.new';
import { isCountryCodeString } from 'pricing-pages-api/currency/types';
import { getCountryCodeFromLocale } from '../../utils/countryCodeUtils';
import { logApiError, logApiSuccess } from '../../utils/apiLogging';
import { isValidProductLine, isValidSkuCategoryName } from '../../utils/typeguards';
import { useIsInAppAuth } from '../user/useUserInfo';
const useProductDetailsQuery = (isAppAuth = false) => isAppAuth ? GET_PRODUCTS_V2_QUERY : GET_PUBLIC_PRODUCTS_V2_QUERY;
export const getIndividualProductDetailsFromProductsQuery = ({
  productApiName,
  productsByName
}) => {
  return productsByName === null || productsByName === void 0 ? void 0 : productsByName[productApiName];
};
export const useProductDetailsForArray = ({
  productApiNames,
  skip
}) => {
  var _data$result;
  const isAppAuth = useIsInAppAuth();
  const version = 'v2';
  const query = useProductDetailsQuery(isAppAuth);
  const queryName = isAppAuth ? 'GET_PRODUCTS_V2_QUERY' : 'GET_PUBLIC_PRODUCTS_V2_QUERY';
  const rawCountryCode = getCountryCodeFromLocale();
  const countryCode = isCountryCodeString(rawCountryCode) ? rawCountryCode : undefined;
  const handleCompleted = useCallback(() => {
    logApiSuccess({
      queryName,
      operation: 'getProducts',
      version
    });
  }, [queryName, version]);
  const handleError = useCallback(error => {
    logApiError(error, {
      queryName,
      operation: 'getProducts',
      version
    });
  }, [queryName, version]);
  const {
    data,
    loading,
    error
  } = useQuery(query, {
    variables: Object.assign({
      productApiNames
    }, !isAppAuth && {
      countryCode
    }),
    skip: skip || productApiNames.length === 0,
    onCompleted: handleCompleted,
    onError: handleError
  });
  if (productApiNames.length === 0) {
    return {
      productDetails: {
        productsByName: {}
      },
      loading: false,
      error: undefined
    };
  }
  const productsByName = data === null || data === void 0 || (_data$result = data.result) === null || _data$result === void 0 ? void 0 : _data$result.productsByName;
  return {
    productDetails: productsByName ? {
      productsByName
    } : undefined,
    loading,
    error
  };
};
export const useProductDetails = ({
  productApiName
} = {}) => {
  var _productDetails$produ;
  const {
    productDetails,
    loading,
    error
  } = useProductDetailsForArray({
    productApiNames: productApiName ? [productApiName] : [],
    skip: !productApiName
  });
  if (!productApiName) return {
    productDetails: undefined,
    loading: false,
    error: undefined
  };
  return {
    productDetails: productDetails === null || productDetails === void 0 || (_productDetails$produ = productDetails.productsByName) === null || _productDetails$produ === void 0 ? void 0 : _productDetails$produ[productApiName],
    loading,
    error
  };
};
export const useIsCoreSeatSku = ({
  productApiName
}) => {
  const {
    productDetails
  } = useProductDetails({
    productApiName
  });
  return (productDetails === null || productDetails === void 0 ? void 0 : productDetails.skuCategoryName) === 'CORE_PLATFORM_SEATS';
};
export const useHasProductInProductDetails = ({
  productApiName
}) => {
  const {
    productDetails,
    loading,
    error
  } = useProductDetails({
    productApiName
  });
  return {
    hasProduct: !!productDetails,
    loading,
    error
  };
};
export const useHasAnyCoreSeatsInProductDetails = () => {
  var _productDetails$produ2;
  const {
    productDetails,
    loading
  } = useProductDetailsForArray({
    productApiNames: [Products.CORE_SEATS_STARTER, Products.CORE_SEATS_PROFESSIONAL, Products.CORE_SEATS_ENTERPRISE]
  });
  const hasAnyCoreSeats = Object.keys((_productDetails$produ2 = productDetails === null || productDetails === void 0 ? void 0 : productDetails.productsByName) !== null && _productDetails$produ2 !== void 0 ? _productDetails$produ2 : {}).length > 0;
  return {
    hasAnyCoreSeats,
    loading
  };
};
export const useHasAnySuiteBundlesInProductDetails = () => {
  var _productDetails$produ3;
  const {
    productDetails,
    loading
  } = useProductDetailsForArray({
    productApiNames: [Products.CRM_SUITE_STARTER_BUNDLE, Products.CRM_SUITE_PROFESSIONAL_BUNDLE, Products.CRM_SUITE_ENTERPRISE_BUNDLE]
  });
  const hasAnySuiteBundles = Object.keys((_productDetails$produ3 = productDetails === null || productDetails === void 0 ? void 0 : productDetails.productsByName) !== null && _productDetails$produ3 !== void 0 ? _productDetails$produ3 : {}).length > 0;
  return {
    hasAnySuiteBundles,
    loading
  };
};
export const useBundledSkuCategoryNamesFromApiNames = ({
  productApiNames
}) => {
  const {
    productDetails,
    loading,
    error
  } = useProductDetailsForArray({
    productApiNames
  });
  const {
    bundledSkuCategoryNames,
    bundledProductLines
  } = useMemo(() => {
    var _productDetails$produ4;
    return Object.values((_productDetails$produ4 = productDetails === null || productDetails === void 0 ? void 0 : productDetails.productsByName) !== null && _productDetails$produ4 !== void 0 ? _productDetails$produ4 : {}).reduce((acc, product) => {
      if (product.productLevel === 'FREE') {
        acc.bundledSkuCategoryNames.push(...['MARKETING_PRODUCTS', 'SALES_PRODUCTS', 'SERVICE_HUB_PRODUCTS', 'CMS_HUB_PRODUCTS', 'OPERATIONS_HUB_PRODUCTS']);
        acc.bundledProductLines.push(...['MARKETING', 'SALES', 'SERVICE', 'CMS', 'OPERATIONS']);
        return acc;
      }
      product.bundledProducts.forEach(bundledProduct => {
        if (isValidSkuCategoryName(bundledProduct.skuCategoryName)) {
          acc.bundledSkuCategoryNames.push(bundledProduct.skuCategoryName);
        }
        if (isValidProductLine(bundledProduct.productLine)) {
          acc.bundledProductLines.push(bundledProduct.productLine);
        }
      });
      return acc;
    }, {
      bundledSkuCategoryNames: [],
      bundledProductLines: []
    });
  }, [productDetails]);
  return {
    bundledSkuCategoryNames,
    bundledProductLines,
    loading,
    error
  };
};